import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography, Tooltip, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useDarkMode } from "../context/darkModeContext";

/**
 * Design tokens + tiny primitives for the Sales Performance page.
 * Visual layer only — no data or calculation logic lives here.
 */

export const MONO_FONT =
  "'IBM Plex Mono', ui-monospace, 'SF Mono', Menlo, Consolas, monospace";
export const PAGE_FONT =
  "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

export const useMetricsUi = () => {
  const { darkMode, colors } = useDarkMode();

  const accent = darkMode ? "#5B8DEF" : "#2E5FE8";

  return {
    colors,
    darkMode,
    accent,
    accentSoft: darkMode ? "rgba(91,141,239,0.16)" : "#EAEFFD",
    positive: darkMode ? "#4ED99B" : "#1C7A4E",
    positiveBg: darkMode ? "rgba(28,122,78,0.22)" : "#E7F5EE",
    danger: darkMode ? "#F0808C" : "#C4293A",
    dangerBg: darkMode ? "rgba(196,41,58,0.20)" : "#FCEBEC",
    flat: colors.textSecondary,
    flatBg: darkMode ? "rgba(255,255,255,0.07)" : "#F0F2F5",
    warning: darkMode ? "#E5B567" : "#9A5B00",
    warningBg: darkMode ? "rgba(154,91,0,0.22)" : "#FDF0DA",
    surfaceAlt: darkMode ? "#20263A" : "#F5F6F8",
    borderStrong: darkMode ? "#3A415C" : "#CBD0D9",
    card: {
      backgroundColor: colors.bgPaper,
      border: `1px solid ${colors.border}`,
      borderRadius: "12px",
      boxShadow: darkMode
        ? "0 1px 3px rgba(0,0,0,0.25)"
        : "0 1px 2px rgba(16,24,40,0.05)",
    },
  };
};

/** Reference-style table header cell. */
export const headCellSx = (ui, extra = {}) => ({
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: ui.colors.textSecondary,
  backgroundColor: ui.colors.bgPaper,
  borderBottom: `1px solid ${ui.colors.border}`,
  whiteSpace: "nowrap",
  py: 1.1,
  ...extra,
});

/** Reference-style numeric body cell (right-aligned, tabular numbers). */
export const numCellSx = (ui, extra = {}) => ({
  fontSize: 13,
  color: ui.colors.textPrimary,
  borderBottom: `1px solid ${ui.colors.border}`,
  fontVariantNumeric: "tabular-nums",
  textAlign: "right",
  whiteSpace: "nowrap",
  py: 1.1,
  ...extra,
});

/** Reference-style text body cell. */
export const textCellSx = (ui, extra = {}) => ({
  fontSize: 13,
  color: ui.colors.textPrimary,
  borderBottom: `1px solid ${ui.colors.border}`,
  py: 1.1,
  ...extra,
});

/**
 * Colored delta pill: ▲ +12.5% / ▼ -6.9% / — , like the reference kpi-delta.
 */
export const DeltaPill = ({ comparison, vsLabel, size = "medium" }) => {
  const ui = useMetricsUi();
  const isSmall = size === "small";

  if (!comparison || comparison.change_pct == null) {
    return (
      <Typography
        component="span"
        sx={{ fontSize: isSmall ? 11 : 12, color: ui.colors.textTertiary }}
      >
        —
      </Typography>
    );
  }

  const up = comparison.change_status === "up";
  const down = comparison.change_status === "down";
  const color = up ? ui.positive : down ? ui.danger : ui.flat;
  const bg = up ? ui.positiveBg : down ? ui.dangerBg : ui.flatBg;
  const arrow = up ? "▲" : down ? "▼" : "—";

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.6,
        flexWrap: "wrap",
      }}
    >
      <Box
        component="span"
        sx={{
          fontSize: isSmall ? 10.5 : 11.5,
          fontWeight: 700,
          color,
          backgroundColor: bg,
          borderRadius: "10px",
          px: 0.9,
          py: 0.15,
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
        }}
      >
        {arrow} {comparison.change_pct > 0 ? "+" : ""}
        {comparison.change_pct}%
      </Box>
      {vsLabel && (
        <Typography
          component="span"
          sx={{ fontSize: isSmall ? 10.5 : 11.5, color: ui.colors.textTertiary }}
        >
          vs {vsLabel}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Pill-style tab strip (active pill = solid dark/inverted, like the reference).
 *
 * Pass `scrollable` to keep the strip on one line with horizontal scrolling
 * plus Previous/Next buttons at each end, instead of the default wrapping
 * behavior — for rows that can grow long (e.g. many SKU tabs).
 */
export const PillTabs = ({ tabs, value, onChange, scrollable = false }) => {
  const ui = useMetricsUi();
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    if (!scrollable) return undefined;
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return undefined;
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollable, updateScrollState, tabs.length]);

  const scrollByPage = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction * Math.round(el.clientWidth * 0.8),
      behavior: "smooth",
    });
  };

  const pillsRow = (
    <Box
      ref={scrollable ? scrollRef : undefined}
      sx={{
        display: "flex",
        gap: 0.75,
        alignItems: "center",
        ...(scrollable
          ? { flexWrap: "nowrap", overflowX: "auto", pb: 1 }
          : { flexWrap: "wrap" }),
      }}
    >
      {tabs.map((tab) => {
        const active = tab.value === value;
        const pill = (
          <Box
            key={tab.value}
            component="button"
            type="button"
            disabled={tab.disabled}
            onClick={() => !tab.disabled && onChange(tab.value)}
            sx={{
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: active ? 600 : 500,
              px: 1.9,
              py: 0.9,
              borderRadius: "20px",
              border: `1px solid ${active ? "transparent" : ui.colors.border}`,
              cursor: tab.disabled ? "not-allowed" : "pointer",
              whiteSpace: "nowrap",
              transition: "background .12s, color .12s, border-color .12s",
              backgroundColor: active
                ? ui.darkMode
                  ? "#E8EBF5"
                  : "#171A21"
                : ui.colors.bgPaper,
              color: active
                ? ui.darkMode
                  ? "#171A21"
                  : "#FFFFFF"
                : tab.disabled
                  ? ui.colors.textTertiary
                  : ui.colors.textSecondary,
              opacity: tab.disabled ? 0.65 : 1,
              "&:hover": {
                backgroundColor:
                  active || tab.disabled ? undefined : ui.surfaceAlt,
              },
            }}
          >
            {tab.label}
            {tab.badge != null && (
              <Box component="span" sx={{ opacity: 0.65, ml: 0.6 }}>
                {tab.badge}
              </Box>
            )}
          </Box>
        );
        return tab.tooltip ? (
          <Tooltip key={tab.value} title={tab.tooltip} arrow>
            <span>{pill}</span>
          </Tooltip>
        ) : (
          pill
        );
      })}
    </Box>
  );

  if (!scrollable) return pillsRow;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 0 }}>
      <IconButton
        size="small"
        onClick={() => scrollByPage(-1)}
        disabled={!canScrollLeft}
        aria-label="Scroll tabs left"
        sx={{ flexShrink: 0, p: 0.5, opacity: canScrollLeft ? 1 : 0.3 }}
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>{pillsRow}</Box>
      <IconButton
        size="small"
        onClick={() => scrollByPage(1)}
        disabled={!canScrollRight}
        aria-label="Scroll tabs right"
        sx={{ flexShrink: 0, p: 0.5, opacity: canScrollRight ? 1 : 0.3 }}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

/**
 * Section heading with optional right-side content.
 * Pass `center` to balance headings that have no `right` content — otherwise
 * they read as flush-left against an empty right half of the card.
 */
export const SectionHead = ({
  title,
  subtitle,
  right,
  center = false,
  sx,
}) => {
  const ui = useMetricsUi();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: center ? "center" : "space-between",
        flexWrap: "wrap",
        gap: 1,
        mb: 1.25,
        ...sx,
      }}
    >
      <Box sx={{ textAlign: center ? "center" : "left" }}>
        <Typography
          sx={{ fontSize: 14, fontWeight: 700, color: ui.colors.textPrimary }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            sx={{ fontSize: 12, color: ui.colors.textSecondary, mt: 0.25 }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      {right}
    </Box>
  );
};

/** Small legend swatch + label, like the reference trend legend. */
export const LegendItem = ({ color, label, line = false }) => {
  const ui = useMetricsUi();
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.7 }}>
      <Box
        component="span"
        sx={{
          width: line ? 14 : 9,
          height: line ? 2.5 : 9,
          borderRadius: line ? "2px" : "2px",
          backgroundColor: color,
          display: "inline-block",
        }}
      />
      <Typography
        component="span"
        sx={{ fontSize: 11.5, color: ui.colors.textSecondary }}
      >
        {label}
      </Typography>
    </Box>
  );
};
